import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Chart from '../components/Chart';
import MetricCard from '../components/MetricCard';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiTarget, FiUsers, FiMessageCircle, FiArrowRight, FiCheckCircle } = FiIcons;

const ServiceGaps = () => {
  const [selectedGap, setSelectedGap] = useState('all');

  const gapMetrics = [
    {
      title: 'Expectation Gaps',
      value: '34',
      change: '8%',
      changeType: 'negative',
      icon: FiTarget,
      color: 'danger',
      subtitle: 'Customer vs Reality'
    },
    {
      title: 'Service Design Issues',
      value: '12',
      change: '2%',
      changeType: 'positive',
      icon: FiAlertTriangle,
      color: 'warning',
      subtitle: 'Process flaws identified'
    },
    {
      title: 'Staff Performance',
      value: '8',
      change: '15%',
      changeType: 'negative',
      icon: FiUsers,
      color: 'primary',
      subtitle: 'Training opportunities'
    },
    {
      title: 'Communication Gaps',
      value: '19',
      change: '5%',
      changeType: 'positive',
      icon: FiMessageCircle,
      color: 'secondary',
      subtitle: 'Messaging clarity'
    }
  ];

  const gapTrendsData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    series: [
      {
        name: 'Expectation Gap',
        type: 'line',
        data: [45, 42, 38, 35, 32, 34],
        smooth: true,
        lineStyle: { color: '#ef4444' }
      },
      {
        name: 'Design Gap',
        type: 'line',
        data: [18, 16, 14, 13, 12, 12],
        smooth: true,
        lineStyle: { color: '#f59e0b' }
      },
      {
        name: 'Performance Gap',
        type: 'line',
        data: [15, 12, 10, 9, 7, 8],
        smooth: true,
        lineStyle: { color: '#0ea5e9' }
      },
      {
        name: 'Communication Gap',
        type: 'line',
        data: [25, 23, 21, 20, 18, 19],
        smooth: true,
        lineStyle: { color: '#64748b' }
      }
    ]
  };

  const priorityMatrix = {
    high: [
      {
        title: 'Response Time Expectations',
        description: 'Customers expect 2-hour response, actual average is 6 hours',
        impact: 'High',
        effort: 'Medium',
        affectedCustomers: 245,
        category: 'Expectation Gap'
      },
      {
        title: 'Mobile App Performance',
        description: 'App crashes during peak hours, causing service disruption',
        impact: 'High',
        effort: 'High',
        affectedCustomers: 189,
        category: 'Service Design'
      }
    ],
    medium: [
      {
        title: 'Staff Product Knowledge',
        description: 'Support team lacks deep product knowledge for complex queries',
        impact: 'Medium',
        effort: 'Medium',
        affectedCustomers: 156,
        category: 'Performance Gap'
      },
      {
        title: 'Pricing Communication',
        description: 'Unclear pricing structure leads to billing confusion',
        impact: 'Medium',
        effort: 'Low',
        affectedCustomers: 123,
        category: 'Communication Gap'
      }
    ],
    low: [
      {
        title: 'Documentation Updates',
        description: 'Help articles are outdated and don\'t reflect current features',
        impact: 'Low',
        effort: 'Low',
        affectedCustomers: 89,
        category: 'Communication Gap'
      }
    ]
  };

  const recommendedActions = [
    {
      title: 'Implement SLA Monitoring',
      description: 'Set up automated alerts for response time breaches',
      priority: 'High',
      timeline: '2 weeks',
      owner: 'Customer Success'
    },
    {
      title: 'Staff Training Program',
      description: 'Comprehensive product knowledge training for support team',
      priority: 'Medium',
      timeline: '1 month',
      owner: 'HR & Training'
    },
    {
      title: 'Communication Audit',
      description: 'Review all customer-facing messaging for clarity',
      priority: 'Medium',
      timeline: '3 weeks',
      owner: 'Marketing'
    }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Service Quality Gaps</h1>
          <p className="text-secondary-600 mt-1">Identify and address gaps between customer expectations and service delivery</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select 
            value={selectedGap}
            onChange={(e) => setSelectedGap(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Gaps</option>
            <option value="expectation">Expectation Gaps</option>
            <option value="design">Design Gaps</option>
            <option value="performance">Performance Gaps</option>
            <option value="communication">Communication Gaps</option>
          </select>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
          >
            Generate Action Plan
          </motion.button>
        </div>
      </div>

      {/* Gap Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {gapMetrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Gap Trends Chart */}
      <Chart 
        title="Service Gap Trends Over Time"
        data={gapTrendsData}
        type="line"
        height={400}
      />

      {/* Priority Matrix */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-6">Priority Matrix</h3>
        
        <div className="space-y-6">
          {/* High Priority */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-danger-500 rounded-full"></div>
              <h4 className="font-semibold text-danger-700">High Priority</h4>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {priorityMatrix.high.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 border border-danger-200 rounded-lg bg-danger-50"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h5 className="font-medium text-secondary-900">{item.title}</h5>
                    <span className="text-xs bg-danger-200 text-danger-800 px-2 py-1 rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <p className="text-sm text-secondary-700 mb-3">{item.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-secondary-600">{item.affectedCustomers} customers affected</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-danger-600">Impact: {item.impact}</span>
                      <span className="text-secondary-600">Effort: {item.effort}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Medium Priority */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-warning-500 rounded-full"></div>
              <h4 className="font-semibold text-warning-700">Medium Priority</h4>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {priorityMatrix.medium.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 border border-warning-200 rounded-lg bg-warning-50"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h5 className="font-medium text-secondary-900">{item.title}</h5>
                    <span className="text-xs bg-warning-200 text-warning-800 px-2 py-1 rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <p className="text-sm text-secondary-700 mb-3">{item.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-secondary-600">{item.affectedCustomers} customers affected</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-warning-600">Impact: {item.impact}</span>
                      <span className="text-secondary-600">Effort: {item.effort}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Low Priority */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-success-500 rounded-full"></div>
              <h4 className="font-semibold text-success-700">Low Priority</h4>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {priorityMatrix.low.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 border border-success-200 rounded-lg bg-success-50"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h5 className="font-medium text-secondary-900">{item.title}</h5>
                    <span className="text-xs bg-success-200 text-success-800 px-2 py-1 rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <p className="text-sm text-secondary-700 mb-3">{item.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-secondary-600">{item.affectedCustomers} customers affected</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-success-600">Impact: {item.impact}</span>
                      <span className="text-secondary-600">Effort: {item.effort}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recommended Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-6">Recommended Actions</h3>
        
        <div className="space-y-4">
          {recommendedActions.map((action, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center space-x-4">
                <div className={`w-2 h-2 rounded-full ${
                  action.priority === 'High' ? 'bg-danger-500' : 'bg-warning-500'
                }`}></div>
                <div>
                  <h4 className="font-medium text-secondary-900">{action.title}</h4>
                  <p className="text-sm text-secondary-600">{action.description}</p>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-secondary-500">
                    <span>Timeline: {action.timeline}</span>
                    <span>Owner: {action.owner}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button className="px-3 py-1 text-xs bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors">
                  Assign
                </button>
                <button className="p-2 text-secondary-400 hover:text-secondary-600 transition-colors">
                  <FiArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceGaps;